import { useState, useEffect } from 'react';
import { streams } from '../../lib/api';

const CAMERA_PRESETS = [
  {
    label: 'CALF Gen1 (RTSP pull)',
    apply: (ip, name) => ({
      rtspUrl: `rtsp://${ip}/stream`,
      serverHost: 'monroe',
      hlsOutputPath: `/opt/stream/output/${name}`,
      serviceName: `hls-${name}`,
      externalUrl: `https://calf.visionimpossible.dk/${name}/stream.m3u8`,
    })
  },
  {
    label: 'CALF Gen2 (RTMP push)',
    apply: (ip, name) => ({
      rtspUrl: `rtmp://${ip}/live/stream`,
      serverHost: 'monroe',
      hlsOutputPath: `/opt/stream/output/${name}`,
      serviceName: `hls-${name}`,
      externalUrl: `https://calf.visionimpossible.dk/${name}/stream.m3u8`,
    })
  },
  {
    label: 'Standard RTSP (port 554)',
    apply: (ip, name) => ({
      rtspUrl: `rtsp://${ip}:554/stream`,
      serverHost: 'monroe',
      hlsOutputPath: `/opt/stream/output/${name}`,
      serviceName: `hls-${name}`,
      externalUrl: `https://calf.visionimpossible.dk/${name}/stream.m3u8`,
    })
  },
  {
    label: 'Generic RTMP push',
    apply: (ip, name) => ({
      rtspUrl: `rtmp://${ip}/live/stream`,
      serverHost: 'monroe',
      hlsOutputPath: `/opt/stream/output/${name}`,
      serviceName: `hls-${name}`,
      externalUrl: `https://calf.visionimpossible.dk/${name}/stream.m3u8`,
    })
  },
  { label: 'Manual', apply: () => ({}) },
];

const EMPTY_FORM = {
  name: '', title: '', description: '',
  rtspUrl: '', serverHost: '', hlsOutputPath: '',
  serviceName: '', externalUrl: '',
  location: '', category: '', tags: [],
  isPublic: true, isEnabled: true,
  _preset: '', _ip: '',
};

function CamerasPage() {
  const [streamList, setStreamList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadStreams(); }, []);

  const loadStreams = async () => {
    try {
      const res = await streams.getAll();
      setStreamList(res.data.streams || []);
    } catch (err) {
      console.error('Failed to load streams:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (stream) => {
    setForm({
      name: stream.name || '',
      title: stream.title || '',
      description: stream.description || '',
      rtspUrl: stream.rtspUrl || '',
      serverHost: stream.serverHost || '',
      hlsOutputPath: stream.hlsOutputPath || '',
      serviceName: stream.serviceName || '',
      externalUrl: stream.externalUrl || '',
      location: stream.location || '',
      category: stream.category || '',
      tags: stream.tags || [],
      isPublic: stream.isPublic ?? true,
      isEnabled: stream.isEnabled ?? true,
    });
    setEditingId(stream.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await streams.update(editingId, form);
      } else {
        await streams.create(form);
      }
      setShowForm(false);
      await loadStreams();
    } catch (err) {
      console.error('Failed to save:', err);
      alert('Failed to save camera configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete camera "${name}"? This cannot be undone.`)) return;
    try {
      await streams.delete(id);
      await loadStreams();
    } catch (err) {
      console.error('Failed to delete:', err);
      alert('Failed to delete camera');
    }
  };

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (loading) return <p className="text-neutral-500">Loading cameras...</p>;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Cameras</h1>
        <button
          onClick={openAdd}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
        >
          Add Camera
        </button>
      </div>

      {/* Camera list */}
      {streamList.length === 0 ? (
        <div className="text-center py-16 text-neutral-500">
          <p className="text-lg mb-2">No cameras configured</p>
          <p className="text-sm">Add a camera to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {streamList.map(stream => (
            <div key={stream.id} className="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
              <div className={`w-3 h-3 rounded-full shrink-0 ${stream.status === 'running' ? 'bg-green-500 animate-pulse' : 'bg-neutral-400'}`} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold">{stream.title || stream.name}</p>
                <p className="text-xs text-neutral-500 truncate">{stream.rtspUrl}</p>
                <p className="text-xs text-neutral-500">{stream.serviceName} &middot; {stream.serverHost}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(stream)} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                  Edit
                </button>
                <button onClick={() => handleDelete(stream.id, stream.name)} className="px-3 py-1.5 text-xs font-semibold rounded-lg text-red-500 border border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950 transition-colors">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <form onSubmit={handleSave} className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 p-6">
            <h2 className="text-xl font-bold mb-4">{editingId ? 'Edit Camera' : 'Add Camera'}</h2>

            <div className="space-y-4">
              <FormField label="Name (internal)" value={form.name} onChange={v => {
                updateForm('name', v);
                // Auto-fill connection fields based on preset + name
                if (!editingId && form._preset && form._preset !== 'Manual' && form._ip) {
                  const p = CAMERA_PRESETS.find(p => p.label === form._preset);
                  if (p) setForm(prev => ({ ...prev, name: v, ...p.apply(prev._ip, v) }));
                }
              }} required placeholder="cam1" />
              <FormField label="Title (public)" value={form.title} onChange={v => updateForm('title', v)} />
              <FormField label="Description" value={form.description} onChange={v => updateForm('description', v)} multiline />

              <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">Connection</p>
                <div className="space-y-4">
                  {!editingId && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-neutral-500 mb-1">Camera Type</label>
                        <select
                          value={form._preset}
                          onChange={e => {
                            const preset = e.target.value;
                            updateForm('_preset', preset);
                            if (preset !== '' && preset !== 'Manual' && form._ip && form.name) {
                              const p = CAMERA_PRESETS.find(p => p.label === preset);
                              if (p) setForm(prev => ({ ...prev, ...p.apply(prev._ip, prev.name) }));
                            }
                          }}
                          className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select camera type...</option>
                          {CAMERA_PRESETS.map(p => <option key={p.label} value={p.label}>{p.label}</option>)}
                        </select>
                      </div>
                      <FormField
                        label="Camera IP Address"
                        value={form._ip}
                        onChange={v => {
                          updateForm('_ip', v);
                          if (form._preset && form._preset !== 'Manual' && form.name) {
                            const p = CAMERA_PRESETS.find(p => p.label === form._preset);
                            if (p) setForm(prev => ({ ...prev, _ip: v, ...p.apply(v, prev.name) }));
                          }
                        }}
                        placeholder="192.168.0.10"
                      />
                    </>
                  )}
                  <FormField label="Camera URL (RTSP/RTMP)" value={form.rtspUrl} onChange={v => updateForm('rtspUrl', v)} placeholder="rtmp://192.168.0.10/live/stream" required />
                  <FormField label="Server Host" value={form.serverHost} onChange={v => updateForm('serverHost', v)} placeholder="monroe" required />
                  <FormField label="HLS Output Path" value={form.hlsOutputPath} onChange={v => updateForm('hlsOutputPath', v)} placeholder="/opt/stream/output/cam1/" required />
                  <FormField label="Service Name (systemd)" value={form.serviceName} onChange={v => updateForm('serviceName', v)} placeholder="hls-cam1" required />
                  <FormField label="External URL" value={form.externalUrl} onChange={v => updateForm('externalUrl', v)} placeholder="https://calf.visionimpossible.dk/cam1/stream.m3u8" />
                </div>
              </div>

              <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">Metadata</p>
                <div className="space-y-4">
                  <FormField label="Location" value={form.location} onChange={v => updateForm('location', v)} />
                  <FormField label="Category" value={form.category} onChange={v => updateForm('category', v)} />
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.isPublic} onChange={e => updateForm('isPublic', e.target.checked)} className="rounded" />
                      Public
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.isEnabled} onChange={e => updateForm('isEnabled', e.target.checked)} className="rounded" />
                      Enabled
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-800">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm font-semibold rounded-lg border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, required, multiline }) {
  const cls = "w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  return (
    <div>
      <label className="block text-xs font-medium text-neutral-500 mb-1">{label}</label>
      {multiline ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required} rows={3} className={cls} />
      ) : (
        <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required} className={cls} />
      )}
    </div>
  );
}

export default CamerasPage;
